import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import qs from 'query-string';

// Components
import Heading from '../../Heading';
import Actions from '../../Actions';
import Button from '../../Button';

// Router
import { getRoute } from '../../../entry/routes';

// Icons
import RemoveIcon from '../../../icons/close.svg';
import ReturnIcon from '../../../icons/return.svg';
import FilterIcon from '../../../icons/card-filter.svg';

// Style
import styles from './Ingredients.styl';

const cx = classNames.bind(styles);

export default function Ingredients(props) {
  const {
    className,
    id,
    stockRole,
    include_ingredients,
    isAdding,
    removedIngredientsCount,
    variations,
    dough,
    size,
    stuffedCrust,
    addToCart,
    onRemove,
    onCancel, // уже не используется, но оставляем для совместимости
    onChangeAvailableVariations,
    isPromo,
    variationId,
  } = props;

  const [isIngredientsShown, setIsIngredientsShown] = useState(false);

  const popupRef = useRef(null);

  const removedIngredientsIds = useMemo(
    () =>
      include_ingredients.filter(ingredient => ingredient.removed).map(ingredient => ingredient.id),
    [include_ingredients],
  );

  const queryParams = useMemo(
    () =>
      qs.stringify({
        dough,
        size,
        stuffedCrust,
        removedIngredientsIds,
      }),
    [dough, size, stuffedCrust, removedIngredientsIds],
  );

  useEffect(() => {
    if (isPromo && include_ingredients.length > 1) {
      include_ingredients.slice(1).forEach(ingredient => {
        if (!ingredient.removed) {
          onRemove(ingredient.id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPromo, variationId]);

  const showIngredients = useCallback(() => {
    setIsIngredientsShown(true);
  }, []);

  const handleAddToCart = useCallback(() => {
    return addToCart().then(() => {
      setIsIngredientsShown(false);
    });
  }, [addToCart]);

  const cancel = useCallback(() => {
    if (isPromo) return;
    if (removedIngredientsCount > 0) {
      return onCancel();
    }
    return setIsIngredientsShown(false);
  }, [isPromo, removedIngredientsCount, onCancel]);

  const saveParamsToState = useCallback(() => {
    return onChangeAvailableVariations(id, variations);
  }, [onChangeAvailableVariations, id, variations]);

  const onOutsidePointerDown = useCallback(
    e => {
      if (!isIngredientsShown) return;

      const node = popupRef.current;
      if (!node) return;

      if (!node.contains(e.target)) {
        setIsIngredientsShown(false);
      }
    },
    [isIngredientsShown],
  );

  useEffect(() => {
    if (!isIngredientsShown) return;

    document.addEventListener('pointerdown', onOutsidePointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', onOutsidePointerDown, true);
    };
  }, [isIngredientsShown, onOutsidePointerDown]);

  const handleIngredientClick = useCallback(
    ingredientId => {
      if (isPromo) {
        const clickedIngredient = include_ingredients.find(ing => ing.id === ingredientId);

        if (clickedIngredient && !clickedIngredient.removed) {
          return;
        }

        include_ingredients.forEach(ingredient => {
          if (!ingredient.removed) {
            onRemove(ingredient.id);
          }
        });

        onRemove(ingredientId);
      } else {
        onRemove(ingredientId);
      }
    },
    [isPromo, include_ingredients, onRemove],
  );

  return (
    <div className={cx('Ingredients', className)}>
      <div
        className={cx('Ingredients__button')}
        onClick={showIngredients}
        onKeyDown={e => e.key === 'Enter' && showIngredients()}
        tabIndex={0}
        role="button"
      >
        <FilterIcon className={cx('Ingredients__button-toggle')} />
        {Boolean(removedIngredientsCount) && (
          <div className={cx('Ingredients__button-counter')}>{removedIngredientsCount}</div>
        )}
      </div>

      {isIngredientsShown && <div className={cx('Ingredients__overlay')} />}

      {isIngredientsShown && (
        <div className={cx('Ingredients__popup')} ref={popupRef}>
          <Heading className={cx('Ingredients__title')} level={3} tagName="div">
            <FormattedMessage id="productCard.removeIngredients.title" />
          </Heading>

          <div className={cx('Ingredients__body')}>
            <ul className={cx('Ingredients__list')}>
              {include_ingredients.map(ingredient => (
                <li
                  key={ingredient.id}
                  className={cx('Ingredients__list-item')}
                  onClick={() => handleIngredientClick(ingredient.id)}
                  onKeyDown={e => e.key === 'Enter' && handleIngredientClick(ingredient.id)}
                  role="menuitem"
                  tabIndex={0}
                >
                  <div
                    className={cx('Ingredients__item', {
                      Ingredients__item_removed: ingredient.removed,
                    })}
                  >
                    <div className={cx('Ingredients__item-name')}>{ingredient.name}</div>

                    {ingredient.removed ? (
                      <div className={cx('Ingredients__item-return')}>
                        <ReturnIcon width={16} height={16} />
                      </div>
                    ) : (
                      <div className={cx('Ingredients__item-remove')}>
                        <RemoveIcon width={12} height={12} />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {stockRole !== 'rewards' && (
              <Link
                onClick={saveParamsToState}
                to={`${getRoute('constructorAlias', id)}?${queryParams}&fromProduct=1`}
                className={cx('Ingredients__add')}
              >
                <div className={cx('Ingredients__add-icon')} />
                <FormattedMessage id="button.addIngredients" />
              </Link>
            )}
          </div>

          <div className={cx('Ingredients__footer')}>
            <Actions>
              <Button className={cx('Ingredients__cancel')} link small onClick={cancel}>
                <FormattedMessage id="button.cancel" />
              </Button>

              <Button
                className={cx('Ingredients__cart-button')}
                onClick={handleAddToCart}
                isLoading={isAdding}
              >
                <FormattedMessage id="button.inCart" />
              </Button>
            </Actions>
          </div>
        </div>
      )}
    </div>
  );
}

Ingredients.defaultProps = {
  className: '',
  include_ingredients: [],
  isAdding: false,
  removedIngredientsCount: 0,
  stockRole: '',
  isPromo: false,
};

Ingredients.propTypes = {
  className: PropTypes.string,
  id: PropTypes.number.isRequired,
  variationId: PropTypes.number,
  stockRole: PropTypes.string,
  include_ingredients: PropTypes.array,
  isAdding: PropTypes.bool,
  removedIngredientsCount: PropTypes.number,
  variations: PropTypes.array.isRequired,
  dough: PropTypes.number.isRequired,
  size: PropTypes.number.isRequired,
  stuffedCrust: PropTypes.string.isRequired,
  addToCart: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onChangeAvailableVariations: PropTypes.func.isRequired,
  isPromo: PropTypes.bool,
};
